import { ArrowForwardIcon, DownloadIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Text,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import { TcmEncounter, useDocumentMenu } from "@metriport/shared-internal";
import { buildDayjs } from "@metriport/shared/common/date";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../../../shared/date";
import { useAppContext } from "../../contexts/app";
import { DetailsDisplay } from "@metriport/shared-internal";

type EncounterProps = {
  encounter: TcmEncounter;
  onNoteChange: (note: string, immediate?: boolean) => void;
  onSummaryFilePathChange: (filePath: string) => void;
};

export const EncounterDetails = ({
  encounter,
  onNoteChange,
  onSummaryFilePathChange,
}: EncounterProps) => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const [noteText, setNoteText] = useState(encounter.freetextNote ?? "");
  const [summaryFilePath, setsummaryFilePath] = useState(encounter.dischargeSummaryPath ?? "");
  const { handleDocumentClick, documentMenuComponent } = useDocumentMenu();

  // Sync local state when encounter prop changes
  useEffect(() => {
    setNoteText(encounter.freetextNote ?? "");
  }, [encounter.freetextNote]);

  useEffect(() => {
    setsummaryFilePath(encounter.dischargeSummaryPath ?? "");
  }, [encounter.dischargeSummaryPath]);

  const filePath = encounter.dischargeSummaryPath;
  const isNotFound =
    !filePath && buildDayjs(encounter.createdAt).add(8, "day").isBefore(buildDayjs());

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!filePath) return;

    const syntheticPointerEvent = {
      clientX: event.clientX,
      clientY: event.clientY,
    } as React.PointerEvent<HTMLElement>;

    handleDocumentClick(
      {
        id: encounter.id,
        fileName: filePath,
        fileType: "xml",
      },
      syntheticPointerEvent
    );
  };

  // Prepare details object for DetailsDisplay
  const encounterDetails: { [key: string]: string | undefined } = {
    Location: encounter.facilityName ?? "Not specified",
    Class: encounter.class ?? "Not specified",
    "Latest Event": encounter.latestEvent,
    Datetime: formatDateTime(encounter.dischargeTime ?? encounter.admitTime),
  };

  // Add phone numbers if they exist
  if (encounter.patientPhoneNumbers.length > 0) {
    const phoneLabel = encounter.patientPhoneNumbers.length > 1 ? "Phone Numbers" : "Phone Number";
    encounterDetails[phoneLabel] = encounter.patientPhoneNumbers.join(", ");
  }

  return (
    <Box p={4} display="flex" flexDirection="column" overflow="hidden" height="100%" width="100%">
      {documentMenuComponent}
      <Flex direction="column" height="100%" justifyContent="flex-start">
        <Flex alignItems="center" justifyContent="space-between">
          <Flex direction="column" gap={1}>
            <Heading size="md" color={useColorModeValue("gray.800", "white")}>
              <Text as="span" color={useColorModeValue("blue.700", "blue.200")}>
                {encounter.patientName}
              </Text>
            </Heading>
            <Flex direction="column" gap={1}>
              <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.300")}>
                {encounter.dischargeTime
                  ? `Discharged ${formatDateTime(encounter.dischargeTime)}`
                  : `Admitted ${formatDateTime(encounter.admitTime)}`}
              </Text>
            </Flex>
          </Flex>
          <Button
            rightIcon={<ArrowForwardIcon />}
            colorScheme="blue"
            size="sm"
            flexShrink={0}
            onClick={() => navigate(`/patient/${encounter.patientId}`)}
          >
            See Patient
          </Button>
        </Flex>
        <Divider my={4} borderColor={useColorModeValue("gray.300", "gray.600")} />

        <DetailsDisplay details={encounterDetails} />

        {/* Discharge summary download / status */}
        <Heading size="md" color={useColorModeValue("gray.800", "white")} mt={4} mb={2}>
          Discharge Summary
        </Heading>
        <Flex alignItems="center" justifyContent="space-between" mb={4}>
          {filePath ? (
            <Alert status="success" mt={4}>
              <Flex alignItems="center" justifyContent="space-between" width="100%">
                <Flex alignItems="center">
                  <AlertIcon />
                  Discharge summary found
                </Flex>
                <Button
                  rightIcon={<DownloadIcon />}
                  colorScheme="blue"
                  size="sm"
                  flexShrink={0}
                  onClick={handleDownloadClick}
                >
                  Download
                </Button>
              </Flex>
            </Alert>
          ) : isNotFound ? (
            <Alert status="warning" mt={4}>
              <AlertIcon />
              Discharge summary not found
            </Alert>
          ) : (
            <Alert status="info" mt={4}>
              <AlertIcon />
              Discharge summary lookup is in progress...
            </Alert>
          )}
        </Flex>
        <Divider my={4} borderColor={useColorModeValue("gray.300", "gray.600")} />
        <Heading size="md" color={useColorModeValue("gray.800", "white")} mt={4} mb={2}>
          Notes
        </Heading>
        <Box position="relative">
          <Textarea
            w="full"
            minH="180px"
            placeholder="Add notes..."
            value={noteText}
            onChange={e => {
              setNoteText(e.target.value);
              onNoteChange(e.target.value, false);
            }}
            sx={{
              "&:focus": {
                "& + button": {
                  zIndex: 2,
                },
              },
            }}
          />
          <Button
            position="absolute"
            bottom={2}
            right={2}
            size="sm"
            colorScheme="gray"
            borderRadius="md"
            _hover={{
              transform: "translateY(-1px)",
              bg: "gray.200",
            }}
            transition="transform 0.2s"
            zIndex={1}
            onClick={() => onNoteChange(noteText, true)}
          >
            Save
          </Button>
        </Box>
        {/* Manual discharge to encounter association */}
        {state.isImpersonating && (
          <Box>
            <Divider my={4} borderColor={useColorModeValue("gray.300", "gray.600")} />
            <Heading size="md" color={useColorModeValue("gray.800", "white")} mt={4} mb={2}>
              Discharge Summary File Path
            </Heading>
            <Box position="relative">
              <Textarea
                w="full"
                minH="180px"
                placeholder="Insert the s3 title of the discharge summary document, i.e. cxId/patientId/cxId_patientId_docId.xml"
                value={summaryFilePath}
                onChange={e => {
                  setsummaryFilePath(e.target.value);
                }}
                sx={{
                  "&:focus": {
                    "& + button": {
                      zIndex: 2,
                    },
                  },
                }}
              />
              <Button
                position="absolute"
                bottom={2}
                right={2}
                size="sm"
                colorScheme="gray"
                borderRadius="md"
                _hover={{
                  transform: "translateY(-1px)",
                  bg: "gray.200",
                }}
                transition="transform 0.2s"
                zIndex={1}
                onClick={() => onSummaryFilePathChange(summaryFilePath)}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
        <Divider my={4} borderColor={useColorModeValue("gray.300", "gray.600")} />

        {/* Encounter ID at the bottom */}
        <HStack mt="auto" justifyContent={"flex-end"}>
          <Text
            fontSize="xs"
            color={useColorModeValue("gray.600", "gray.300")}
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
          >
            Encounter {encounter.id}
          </Text>
        </HStack>
      </Flex>
    </Box>
  );
};
